import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';

import { DisplayLanguage, supportedLocales } from '@app/services/translations';
import { selectLocale, setLocaleState } from '@app/redux/locale';
import { Domains, GeneralEventTypes } from '@app/types';

export const useSwitchLanguage = () => {
  const dispatch = useDispatch();
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const { i18n } = useTranslation('');

  const switchLanguage = useMemo(() => {
    return (desiredLanguage: DisplayLanguage): { wasSuccessful: boolean } => {
      // Check if this language is available to this user given their locale
      const availableLanguages = supportedLocales[userLocale].availableLanguages;

      if (!availableLanguages.find(({ displayLanguage }) => displayLanguage === desiredLanguage)) {
        return { wasSuccessful: false };
      }

      // Change to the desired language
      Cookies.set('displayLanguage', desiredLanguage, { expires: 14 });

      i18n.changeLanguage(desiredLanguage);
      dispatch(setLocaleState({ displayLanguage: desiredLanguage }));

      postChangeLanguageMessage(desiredLanguage);

      return { wasSuccessful: true };
    };
  }, [userLocale, dispatch, i18n]);

  return switchLanguage;
};

function postChangeLanguageMessage(displayLanguage: DisplayLanguage) {
  const mainframe = document.getElementById('webIframe');
  const wnd = (mainframe as HTMLIFrameElement)?.contentWindow;

  if (mainframe && wnd) {
    wnd.postMessage(
      {
        domain: Domains.General,
        data: displayLanguage,
        eventType: GeneralEventTypes.ChangeLanguage,
      },
      '*'
    );
  }
}
